import React from "react";
import JobsService from "../../services/JobsService";
import JobEntity from "../../types/jobEntity.type";
import EmptyPostedJobsView from "./EmptyPostedJobsView";
import JobList from "./JobsList";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";

type Props = {
  userId?: number | null;
  fullName?: string | null;
};

type State = {
  jobEntity: JobEntity[];
  editingJob: JobEntity | null;
  errorMessage?: string;
};

export default class JobsView extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      jobEntity: [],
      editingJob: null,
      errorMessage: undefined,
    };
  }

  componentDidMount() {
    if (this.props.userId != null && this.props.userId > 0) {
      JobsService.getUserJob(this.props.userId)
        .then((response) => {
          console.log("API response (getUserJob):", response.data);
          const jobs = Array.isArray(response.data) ? response.data : [];
          this.setState({ jobEntity: jobs, errorMessage: undefined });
        })
        .catch((error) => {
          const errorMsg =
            error.response?.data?.message || error.message || "Failed to fetch user jobs";
          console.error("Fetch error (getUserJob):", errorMsg);
          this.setState({ jobEntity: [], errorMessage: errorMsg });
        });
    } else {
      JobsService.getJobsBoard()
        .then((response) => {
          console.log("API response (getJobsBoard):", response.data);
          const jobs = Array.isArray(response.data) ? response.data : [];
          this.setState({ jobEntity: jobs, errorMessage: undefined });
        })
        .catch((error) => {
          const errorMsg =
            error.response?.data?.message || error.message || "Failed to fetch jobs board";
          console.error("Fetch error (getJobsBoard):", errorMsg);
          this.setState({ jobEntity: [], errorMessage: errorMsg });
        });
    }
  }

  handleEdit = (job: JobEntity) => {
    console.log("Editing job:", job);
    this.setState({ editingJob: job });
  };

  handleDelete = (jobId: number) => {
    if (window.confirm("Are you sure you want to delete this job?")) {
      JobsService.deleteJob(jobId)
        .then(() => {
          console.log("Deleted job ID:", jobId);
          this.setState((prevState) => ({
            jobEntity: prevState.jobEntity.filter((j) => j.id !== jobId),
            errorMessage: undefined,
          }));
        })
        .catch((error) => {
          const errorMsg =
            error.response?.data?.message || error.message || "Failed to delete job";
          console.error("Delete error:", errorMsg);
          this.setState({ errorMessage: errorMsg });
        });
    }
  };

  handleSaveJob = (updatedJob: JobEntity) => {
    console.log("Saving job:", updatedJob);
    JobsService.updateJob(updatedJob)
      .then(() => {
        this.setState((prevState) => ({
          jobEntity: prevState.jobEntity.map((j) =>
            j.id === updatedJob.id ? updatedJob : j
          ),
          editingJob: null,
          errorMessage: undefined,
        }));
      })
      .catch((error) => {
        const errorMsg =
          error.response?.data?.message || error.message || "Failed to update job";
        console.error("Update error:", errorMsg);
        this.setState({ errorMessage: errorMsg });
      });
  };

  render() {
    let view = null;

    console.log("jobEntity:", this.state.jobEntity);
    console.log("editingJob:", this.state.editingJob);

    if (Array.isArray(this.state.jobEntity) && this.state.jobEntity.length > 0) {
      view = (
        <JobList
          key={this.props.userId}
          listItems={this.state.jobEntity}
          onEdit={this.handleEdit}
          onDelete={this.handleDelete}
        />
      );
    } else {
      view = <EmptyPostedJobsView key={this.props.userId} fullName={this.props.fullName} />;
    }

    return (
      <div className="container">
        {this.state.errorMessage && (
          <div className="alert alert-danger">{this.state.errorMessage}</div>
        )}
        {view}
        {this.state.editingJob && (
          <>
            <div
              className="modal-backdrop-custom"
              style={modalBackdropStyle}
              onClick={() => this.setState({ editingJob: null })}
            ></div>
            <EditJobModal
              job={this.state.editingJob}
              onSave={this.handleSaveJob}
              onCancel={() => this.setState({ editingJob: null })}
            />
          </>
        )}
      </div>
    );
  }
}

const modalBackdropStyle: React.CSSProperties = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  zIndex: 1000,
};

interface EditJobModalProps {
  job: JobEntity;
  onSave: (updatedJob: JobEntity) => void;
  onCancel: () => void;
}

const EditJobModal: React.FC<EditJobModalProps> = ({ job, onSave, onCancel }) => {
  console.log("Rendering EditJobModal with job:", job);

  const validationSchema = Yup.object({
    title: Yup.string().required("Title is required"),
    description: Yup.string().nullable(),
    user_id: Yup.number().nullable(),
    applyer_id: Yup.number().nullable(),
    longitude: Yup.number().nullable(),
    latitude: Yup.number().nullable(),
    is_done: Yup.boolean().nullable(),
  });

  return (
    <div className="modal-custom" style={modalStyle}>
      <div style={{ position: "relative" }}>
        <h2>Edit Job</h2>
        <button
          type="button"
          className="btn-close-custom"
          onClick={onCancel}
          style={{ position: "absolute", top: "10px", right: "10px", fontSize: "1.2em" }}
        >
          ×
        </button>
        <Formik
          initialValues={{
            title: job.title || "",
            description: job.description || "",
            user_id: job.user_id || 0,
            applyer_id: job.applyer_id || 0,
            longitude: job.longitude || 0.0,
            latitude: job.latitude || 0.0,
            is_done: job.is_done || false,
          }}
          validationSchema={validationSchema}
          onSubmit={(values) => onSave({ ...values, id: job.id })}
        >
          {({ isSubmitting }) => (
            <Form>
              <div className="form-group">
                <label>Title</label>
                <Field name="title" type="text" className="form-control" />
                <ErrorMessage name="title" component="div" className="text-danger" />
              </div>
              <div className="form-group">
                <label>Description</label>
                <Field name="description" type="text" className="form-control" />
                <ErrorMessage name="description" component="div" className="text-danger" />
              </div>
              <div className="form-group">
                <label>Adder ID</label>
                <Field name="user_id" type="number" className="form-control" />
                <ErrorMessage name="user_id" component="div" className="text-danger" />
              </div>
              <div className="form-group">
                <label>Applyer ID</label>
                <Field name="applyer_id" type="number" className="form-control" />
                <ErrorMessage name="applyer_id" component="div" className="text-danger" />
              </div>
              <div className="form-group">
                <label>Longitude</label>
                <Field name="longitude" type="number" className="form-control" />
                <ErrorMessage name="longitude" component="div" className="text-danger" />
              </div>
              <div className="form-group">
                <label>Latitude</label>
                <Field name="latitude" type="number" className="form-control" />
                <ErrorMessage name="latitude" component="div" className="text-danger" />
              </div>
              <div className="form-group">
                <label>Is Done</label>
                <Field name="is_done" type="checkbox" className="form-control" />
                <ErrorMessage name="is_done" component="div" className="text-danger" />
              </div>
              <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                Save
              </button>
              <button type="button" className="btn btn-secondary" onClick={onCancel}>
                Cancel
              </button>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

const modalStyle: React.CSSProperties = {
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  backgroundColor: "white",
  padding: "20px",
  zIndex: 1100,
  borderRadius: "8px",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
  maxWidth: "600px",
  width: "90%",
  minWidth: "300px",
  minHeight: "200px",
  maxHeight: "80vh",
  overflowY: "auto",
  display: "block",
  visibility: "visible",
};